<script lang="ts">
    import { tweened } from 'svelte/motion';
    import Question from './Question.svelte';
    import Score from './Score.svelte';
    import { shuffle } from './utils.ts';
    import * as quiz_questions_raw from '../questions.json';

    enum State {
        StartScreen,
        Quizzing,
        Finale
    };

    let quiz: object[];
    let current_question_index: number;
    let current_state: State;
    let current_question: string;
    $: current_question = quiz[current_question_index].question;
    let current_answers: string[];
    $: current_answers = quiz[current_question_index].answers;
    let correct_answers_count: number;

    let displayed_questions = new Set();

    const quiz_progress = tweened(0);
    $: switch(current_state) {
        case State.StartScreen:
            quiz_progress.set(0);
            break;

        case State.Quizzing:
            if(current_question_index == 0) {
                // Skip the tweening effect when going back to retry the quiz
                quiz_progress.set(0, { duration: 0 });
            } else {
                quiz_progress.set(current_question_index / quiz.length);
            }
            break;

        case State.Finale:
            quiz_progress.set(1);
            break;
    };

    resetState();

    function prepare_quiz(): object {
        const QUESTIONS_TO_SELECT_FROM_EACH_LEVEL: number = 4;

        let result: object[] = [];

        let easy = quiz_questions_raw.easy.concat(quiz_questions_raw.additions.easy);
        let medium = quiz_questions_raw.medium.concat(quiz_questions_raw.additions.medium);
        let hard = quiz_questions_raw.hard.concat(quiz_questions_raw.additions.hard);

        for(let questions of [easy, medium, hard]) {
            shuffle(questions);

            let questions_to_add = questions.filter(q => !displayed_questions.has(q));
            questions_to_add = questions_to_add.slice(0, QUESTIONS_TO_SELECT_FROM_EACH_LEVEL);
            if(questions_to_add.length < QUESTIONS_TO_SELECT_FROM_EACH_LEVEL) {
                let repeated_questions = questions.filter(q => displayed_questions.has(q));
                questions_to_add = questions_to_add.concat(repeated_questions.slice(0, QUESTIONS_TO_SELECT_FROM_EACH_LEVEL));
            }
            shuffle(questions_to_add);
            questions_to_add.forEach(q => displayed_questions.add(q));
            result = result.concat(questions_to_add);
        }

        return result;
    }

    function resetState() {
        quiz = prepare_quiz();
        current_question_index = 0;
        current_state = State.StartScreen;
        correct_answers_count = 0;
    }

    function retry() {
        resetState();
        current_state = State.Quizzing;
    }

    function handleAnswerMsg(event) {
        if(event.detail.correct) {
            correct_answers_count += 1;
        }

        if(current_question_index + 1 < quiz.length) {
            current_question_index += 1;
            current_state = State.Quizzing;
        } else {
            current_state = State.Finale;
        }
    };
</script>

<main>
    {#if current_state == State.StartScreen}
        <h1>Test your Fedi knowledge</h1>
        <div class="wrapper">
          <div class="intro">
            <p>On <span>May 18, 2008 🐣</span> the first public post appeared on a site called <i>identi.ca</i> powered by free software. The idea behind it was that anyone could download the source code and run their own social network connected to other nodes. This was the beginning of Fediverse.</p>

            <p>Much time has passed. We now use different software and new federating protocols. <span>Fediverse lives on!</span> ✨ </p>

            <p>🎂 This quiz marks the <span>14th anniversary</span> of our grand social universe! How much do you know about it? Take part in this fun quiz and find out!</p>
          </div>
          <button class="u-action" on:click={() => current_state = State.Quizzing}>Let's go!</button>
        </div>
    {:else if current_state == State.Quizzing}
        <progress
            id="quizProgress"
            value={$quiz_progress}>

            Question {current_question_index + 1} out of {quiz.length}
        </progress>

        <Question
            question={current_question}
            answers={current_answers}
            on:answerMsg={handleAnswerMsg}
            />
    {:else if current_state == State.Finale}
        <h2>All done, congrats!</h2>
        <p class="percent"><span>{Math.floor(100 * correct_answers_count / quiz.length)}%</span> correct answers</p>

        {#if correct_answers_count > 0.9 * quiz.length}
            <Score
                level="Expert"
                level_description="You seem to know everything!"
                badge_description="badge: cute phoenix chilling in a cup of tea"
                prompt="Please contribute to these projects and share your expertise:"
                recommended_sites="{ ['joinfediverse.wiki', 'activitypub.rocks'] }"
                />
        {:else if correct_answers_count > 0.55 * quiz.length}
            <Score
                level="Scholar"
                level_description="You’re quite knowledgeable about Fediverse!"
                badge_description="badge: cute phoenix pointing at the blackboard with complex formulas"
                prompt="But there’s always more to learn! Check out these resources:"
                recommended_sites="{ [ 'delightful.club', 'fediverse.town' ] }"
                />
        {:else}
            <Score
                level="Explorer"
                level_description="You’ve got some learning to do!"
                badge_description="badge: cute phoenix with telescope looking at stars"
                prompt="Luckily, there’re many resources about federating networks:"
                recommended_sites="{ [ 'joinfediverse.wiki', 'fediverse.info' ] }"
                />
        {/if}

        <button class="u-action" on:click={() => retry()}>Try again</button>
    {/if}
</main>
{#if current_state == State.Finale}
    <footer>
        <p class="share">Share the screenshot of your result with others!</p>
        <p><a href="https://codeberg.org/fediverse/fediquiz">Git repository</a></p>
        <p>
            <span class="license"><a href="https://creativecommons.org/licenses/by-nc-sa/4.0/">CC BY-NC-SA 4.0</a> for content,</span>
            <span class="license"><a href="https://www.gnu.org/licenses/agpl-3.0.en.html">AGPL 3.0</a> for code</span>
        </p>
        <p>Made by <a href="https://mastodon.xyz/@lightone">lostinlight</a> and <a href="https://functional.cafe/@minoru">minoru</a></p>
    </footer>
{/if}

<style lang="scss">
    @import './styles/App.scss';
</style>
